// Pure rekenfuncties voor de Declaraties-module (module C4).
//
// Géén berekeningen in routes of componenten — alle status-, som- en
// telling-logica voor declaraties komt hier samen.
//
// Conventies:
//   - State-machine in één tabel `DECLARATIE_STATE_TRANSITIONS`.
//   - Sommen worden afgerond op 2 decimalen (cent-precisie); bedragen komen
//     binnen als JS-numbers, niet als Decimal — caller is verantwoordelijk
//     voor decimal→number-conversie (Number(prismaDecimal) is gangbaar).
//   - Geen Date-logica in deze module — datum-string formatting/parsing zit
//     in routes (zoals in sick-leave.ts).

// ────────────────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────────────────

/** Mirror van Prisma DeclaratieStatus enum als string-literal union. */
export type DeclaratieStatusValue =
  | 'CONCEPT'
  | 'INGEDIEND'
  | 'GOEDGEKEURD_TEAMLEIDER'
  | 'AFGEKEURD_TEAMLEIDER'
  | 'GOEDGEKEURD_HR'
  | 'AFGEKEURD_HR'
  | 'UITBETAALD'
  | 'INGETROKKEN'
  // FO-055 (batch-17) P2 — aanvullende vraag (geen afwijzing): tijdelijk bij de
  // medewerker, keert na antwoord terug naar INGEDIEND.
  | 'AANVULLENDE_VRAAG'

// ────────────────────────────────────────────────────────────────────────────
// State-machine
// ────────────────────────────────────────────────────────────────────────────

/**
 * Toegestane status-overgangen voor een declaratie.
 *
 * Workflow:
 *   CONCEPT → INGEDIEND → GOEDGEKEURD_TEAMLEIDER → GOEDGEKEURD_HR → UITBETAALD
 *
 * Side-paden:
 *   - Afkeuren door TL: INGEDIEND → AFGEKEURD_TEAMLEIDER (terug naar CONCEPT
 *     mogelijk).
 *   - Afkeuren door HR: GOEDGEKEURD_TEAMLEIDER → AFGEKEURD_HR.
 *   - Intrekken: vanaf CONCEPT/INGEDIEND/GOEDGEKEURD_TEAMLEIDER mogelijk.
 *   - Withdraw flow: INGEDIEND/GOEDGEKEURD_TEAMLEIDER → CONCEPT (her-bewerken).
 *
 * UITBETAALD is end-state. AFGEKEURD_* en INGETROKKEN zijn ook end-states
 * (geen transities meer — eventueel re-creëren als nieuwe declaratie).
 */
export const DECLARATIE_STATE_TRANSITIONS: Record<
  DeclaratieStatusValue,
  DeclaratieStatusValue[]
> = {
  CONCEPT: ['INGEDIEND', 'INGETROKKEN'],
  INGEDIEND: [
    'GOEDGEKEURD_TEAMLEIDER',
    'AFGEKEURD_TEAMLEIDER',
    'AANVULLENDE_VRAAG', // FO-055 P2 — TL/HR-beoordelaar stelt een vraag
    'CONCEPT', // withdraw
    'INGETROKKEN',
  ],
  GOEDGEKEURD_TEAMLEIDER: [
    'GOEDGEKEURD_HR',
    'AFGEKEURD_HR',
    'AANVULLENDE_VRAAG', // FO-055 P2 — HR-beoordelaar stelt een vraag
    'CONCEPT', // withdraw
    'INGETROKKEN',
  ],
  AFGEKEURD_TEAMLEIDER: ['CONCEPT'], // medewerker mag bijwerken en opnieuw indienen
  GOEDGEKEURD_HR: ['UITBETAALD'],
  AFGEKEURD_HR: ['CONCEPT'], // medewerker mag bijwerken en opnieuw indienen
  UITBETAALD: [], // end-state
  INGETROKKEN: [], // end-state
  // FO-055 P2 — na het antwoord van de medewerker keert de declaratie terug naar
  // INGEDIEND in de beoordeel-inbox (geen afwijzing, blijft in behandeling).
  AANVULLENDE_VRAAG: ['INGEDIEND'],
}

/**
 * True als overgang van `from` naar `to` is toegestaan volgens de
 * state-machine.
 */
export function canTransitionTo(
  from: DeclaratieStatusValue,
  to: DeclaratieStatusValue,
): boolean {
  return DECLARATIE_STATE_TRANSITIONS[from].includes(to)
}

// ────────────────────────────────────────────────────────────────────────────
// Aggregaties
// ────────────────────────────────────────────────────────────────────────────

/** Rond een number af op cent-precisie (2 decimalen). */
function round2(n: number): number {
  return Math.round(n * 100) / 100
}

/**
 * Som per declaratie-categorie + totaal.
 *
 * Gebruikt voor backoffice-dashboards en export per type. Onbekende `type`-
 * keys worden meegenomen onder hun eigen key (geen filter), zodat ook
 * legacy-records correct optellen.
 */
export function sumByType(
  declaraties: Array<{ type: string; bedrag: number }>,
): { byType: Record<string, number>; total: number } {
  const byType: Record<string, number> = {}
  let total = 0
  for (const d of declaraties) {
    const cur = byType[d.type] ?? 0
    byType[d.type] = round2(cur + d.bedrag)
    total = round2(total + d.bedrag)
  }
  return { byType, total }
}

/**
 * Aantal declaraties per status. Geeft altijd ALLE statussen terug
 * (0 als geen records) zodat de UI niet hoeft te checken op `undefined`.
 */
export function statusCount(
  declaraties: Array<{ status: DeclaratieStatusValue }>,
): Record<DeclaratieStatusValue, number> {
  const out: Record<DeclaratieStatusValue, number> = {
    CONCEPT: 0,
    INGEDIEND: 0,
    GOEDGEKEURD_TEAMLEIDER: 0,
    AFGEKEURD_TEAMLEIDER: 0,
    GOEDGEKEURD_HR: 0,
    AFGEKEURD_HR: 0,
    UITBETAALD: 0,
    INGETROKKEN: 0,
    AANVULLENDE_VRAAG: 0,
  }
  for (const d of declaraties) {
    out[d.status] = (out[d.status] ?? 0) + 1
  }
  return out
}
